/**
 * DS_04 — Collection filter experience (quick chips + slide-in panel) for
 * /collections/all. Theme-native port of the won Intelligems variant.
 *
 * The chips and panel are server-rendered in snippets/ds04-collection-filters.liquid
 * (OUTSIDE #CollectionAjaxContent, so AJAX swaps never destroy them) and this class
 * MOVES them to the won injection points: the panel into `.grid__item--sidebar`,
 * the chips into `.grid__item--content` on desktop. The theme's native
 * `.filter-wrapper` (Availability + Price form) is relocated into the panel.
 *
 * Filtering model (won behavior):
 *   - type / concern / position / badge: instant client-side hide via a class on
 *     `.product-card-v4`. OR within a group, AND across groups (a card must satisfy
 *     every active group). Card taxonomy comes from data-filter-* attributes
 *     (snippets/product-card-v4.liquid).
 *   - price: instant client-side filtering too, on the card's displayed price
 *     (data-filter-price, in cents). An AND constraint on top of the taxonomy union.
 *     No server round-trip — the curated /collections/all is a single page, so there
 *     is nothing off-page to miss (same assumption as the taxonomy filters).
 *   - Untagged cards (all four lists empty) are ALWAYS visible — the won "Other"
 *     type behavior and the safe off-state for not-yet-tagged products.
 *
 * Re-render survival: theme.js swaps #CollectionAjaxContent.innerHTML wholesale on
 * every native filter render (no post-render event exists), so a MutationObserver
 * on that container re-adopts the held panel/chips nodes, pulls in the FRESH
 * `.filter-wrapper`, re-applies filter state, and re-fixes the count text (which
 * theme.js rewrites with a bare number).
 *
 * Deviations from the won code (all intentional, see build brief):
 *   - Data from product_type + ds-* tags, with the won variant's hardcoded catalog
 *     kept only as a per-product FALLBACK (FALLBACK_TAXONOMY) for products that have
 *     no ds-* tags yet — so the filters match production before the catalog is tagged.
 *   - Canonical slugs in data-value attributes replace the runtime label
 *     normalization chain (e.g. "Side Sleepers" -> "side").
 *   - Best Sellers keys on data-filter-badge (won kept it in the type array).
 *   - Pagination bail-out guard: client-side hiding only sees the CURRENT page.
 *     /collections/all is a curated single page today (20 products, paginate_by=40
 *     in main-collection-v4.liquid); if it ever paginates, the feature disables
 *     itself and leaves native filtering untouched.
 *   - Intelligems yield guard: while the original DS_04 experiment is still being
 *     served by Intelligems (window.optiActiveTests.DS_04 set, or its .ds_04_filter
 *     markup present), this port stays dormant so the two versions never fight over
 *     the same DOM. Turning the experiment off in Intelligems activates the port —
 *     no code change needed. `?ds04-force=1` overrides the guard for QA.
 *   - `window.optiReady` / `optiActiveTests` registration and the analytics block
 *     are dropped.
 */

const DESKTOP_MIN_WIDTH = 769;
const POLL_INTERVAL = 250;
const POLL_TIMEOUT = 4000;
const FADE_MS = 200;
const DRAWER_OPEN_CLASS = 'ds04-drawer-open';
const CARD_HIDDEN_CLASS = 'ds04-card-hidden';
const GRID_FADED_CLASS = 'ds04-grid-faded';
const OPTION_ACTIVE_CLASS = 'ds04__option--active';
const CHIP_ACTIVE_CLASS = 'ds04__chip--active';

// Fallback taxonomy, ported from the won Intelligems DS_04 variant's hardcoded
// product catalog. Used ONLY for products that carry no ds-* tags (data-ds-untagged),
// so the filters reproduce production today; once a product is tagged in Shopify, its
// tags fully own its taxonomy and this map is ignored for it. Delete an entry (or the
// whole map) as products get tagged. Keyed by product handle.
const FALLBACK_TAXONOMY = {
  'contoured-orthopedic-pillow': { type: ['pillows'], concern: ['neck-pain-relief', 'shoulder-pain'], position: ['side', 'back', 'stomach'], badge: ['best-seller'] },
  'dosaze-adjustable-pillow': { type: ['pillows'], concern: ['neck-pain-relief'], position: ['side', 'back', 'stomach'], badge: ['best-seller'] },
  'dosaze-therapeutic-cooling-wedge-pillow': { type: ['wedge-pillows'], concern: ['neck-pain-relief', 'cooling', 'acid-reflux', 'snoring'], position: ['side', 'back'], badge: [] },
  '100-mulberry-silk-pillowcase-25-momme': { type: ['bedding'], concern: ['luxury'], position: [], badge: [] },
  'alignment-bundle-contoured-orthopedic-pillow-wedge-pillow-kit': { type: ['pillows', 'wedge-pillows'], concern: ['neck-pain-relief', 'acid-reflux', 'snoring'], position: ['side', 'back', 'stomach'], badge: ['best-seller'] },
  'silvertech-pillow-protector': { type: ['bedding'], concern: ['cooling', 'protection'], position: [], badge: ['best-seller'] },
  'ultimate-comfort-bundle': { type: ['pillows', 'bedding'], concern: ['neck-pain-relief', 'cooling', 'protection'], position: ['side', 'back', 'stomach'], badge: ['best-seller'] },
  'dosaze-cooling-mattress-protector': { type: ['bedding'], concern: ['cooling', 'protection'], position: [], badge: ['best-seller'] },
  'cooling-memory-foam-filler-only': { type: [], concern: [], position: [], badge: [] },
  'travel-pillow': { type: ['pillows'], concern: ['neck-pain-relief'], position: ['side', 'back', 'stomach'], badge: ['best-seller'] },
  'cooling-comfort-kit': { type: ['pillows'], concern: ['neck-pain-relief'], position: ['side', 'back', 'stomach'], badge: [] },
  'dosaze-thermacool-adjustable-pillow': { type: ['pillows'], concern: ['neck-pain-relief'], position: ['side', 'back', 'stomach'], badge: ['best-seller'] },
  'dosaze-thermacool-sheet-set-1': { type: ['bedding'], concern: ['cooling'], position: [], badge: ['best-seller'] },
  'dosaze-travel-neck-pillow': { type: ['pillows'], concern: ['neck-pain-relief'], position: [], badge: [] },
  'dosaze™-therapeutic-cooling-wedge-pillow-white': { type: ['pillows'], concern: ['neck-pain-relief'], position: ['side', 'back'], badge: ['best-seller'] },
  'thermacool-comforter': { type: ['bedding'], concern: ['cooling'], position: [], badge: ['best-seller'] },
  'dosaze-luxe-bamboo-sheet-set': { type: ['bedding'], concern: ['luxury'], position: [], badge: [] },
  'dreamalign-back-relief-mattress-topper': { type: ['mattress-topper'], concern: ['back-pain'], position: ['side', 'back', 'stomach'], badge: [] },
  'dreamalign-back-relief-mattress-topper-v2': { type: ['mattress-topper'], concern: ['back-pain'], position: ['side', 'back', 'stomach'], badge: [] },
  'dreamalign-side-sleeper-mattress-topper-3-zoned': { type: ['mattress-topper'], concern: ['back-pain'], position: ['side', 'back', 'stomach'], badge: [] },
  'thermacool-mattress-topper': { type: ['mattress-topper'], concern: ['cooling'], position: ['side', 'back', 'stomach'], badge: [] },
  'dosaze-cloudsoft-quickdry-bath-sheets': { type: ['bath'], concern: [], position: [], badge: [] },
  'dosaze-cloudsoft-quickdry-towels': { type: ['bath'], concern: [], position: [], badge: [] },
  'dosaze-gift-card': { type: [], concern: [], position: [], badge: [] },
};

class Ds04CollectionFilters {
  constructor(root) {
    this.root = root;
    this.sectionRoot = root.closest('.ds04-collection');
    this.ajaxContent = document.getElementById('CollectionAjaxContent');
    this.panel = root.querySelector('[data-ds04-panel]');
    this.chips = root.querySelector('[data-ds04-chips]');
    this.chipsHome = root.querySelector('[data-ds04-chips-home]');
    this.filterBtn = root.querySelector('[data-ds04-drawer-toggle]');
    this.desktopMq = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`);
    this.observer = null;
    this.disabled = false;
    this.forced = new URLSearchParams(window.location.search).has('ds04-force');
    this.state = { type: [], concern: [], position: [], badge: [], price: '' };
    // Canonical slug set per dimension, derived from the rendered panel options so
    // there is one source of truth. Card tags are validated against it (buildVocab
    // tolerates this.panel being null — guarded again in init()).
    this.vocab = this.buildVocab();
    this.warnedTags = new Set();
    this.init();
  }

  init() {
    if (!this.sectionRoot || !this.ajaxContent || !this.panel || !this.chips) {
      return;
    }
    // The Liquid render is already gated to collection.handle == 'all'; this also
    // admits locale-prefixed paths (/en-ca/collections/all), which the won
    // exact-pathname gate excluded entirely.
    if (!/\/collections\/all\/?$/.test(window.location.pathname)) {
      this.root.hidden = true;
      return;
    }
    if (this.intelligemsTestActive()) {
      this.root.hidden = true;
      return;
    }
    if (this.checkPaginationBail()) {
      return;
    }
    this.applyUsdGate();
    this.mount();
    this.bindEvents();
    this.applyFilters(true);
    this.observer = new MutationObserver(() => this.onGridReplaced());
    this.observer.observe(this.ajaxContent, { childList: true });
    this.watchForLateIntelligems();
  }

  /* ── Intelligems yield guard ───────────────────────────────────────────── */

  intelligemsTestActive() {
    if (this.forced) {
      return false;
    }
    return Boolean(
      (window.optiActiveTests && window.optiActiveTests.DS_04) ||
        document.querySelector('.ds_04_filter')
    );
  }

  watchForLateIntelligems() {
    // The Intelligems bundle is async and may inject its DS_04 variant after our
    // init won the race; poll briefly so the overlap is safe in either order.
    this.waitFor(() => this.intelligemsTestActive()).then((active) => {
      if (active) {
        this.disable('the Intelligems DS_04 experiment is serving this session');
      }
    });
  }

  /* ── USD gate (won off-state: non-USD visitors get no price group) ─────── */

  applyUsdGate() {
    const flag = document.querySelector('.toolbar__item .disclosure__toggle [data-flag]');
    const isUsd = Boolean(flag) && flag.getAttribute('data-flag') === 'USD';
    if (!isUsd) {
      const priceGroup = this.panel.querySelector('[data-ds04-group="price"]');
      if (priceGroup) {
        priceGroup.classList.add('ds04__group--hidden');
      }
    }
  }

  /* ── Mounting / relocation ─────────────────────────────────────────────── */

  mount() {
    const sidebar = this.ajaxContent.querySelector('.grid__item--sidebar');
    if (sidebar && this.panel.parentElement !== sidebar) {
      sidebar.insertAdjacentElement('afterbegin', this.panel);
    }
    this.adoptFilterWrapper();
    this.placeChips();
  }

  adoptFilterWrapper() {
    // Always take the wrapper from the DESKTOP sidebar wrap: theme.js
    // cloneFiltersOnMobile() puts a CLONE in #CollectionInlineFilterWrap, so a
    // bare `.filter-wrapper` query can grab the wrong node.
    const fresh = document.querySelector('#CollectionSidebarFilterWrap .filter-wrapper');
    const current = this.panel.querySelector('.filter-wrapper');
    const bottom = this.panel.querySelector('[data-ds04-bottom]');
    if (fresh && bottom && fresh !== current) {
      if (current) {
        current.remove(); // stale copy orphaned by the AJAX innerHTML swap
      }
      bottom.insertAdjacentElement('beforebegin', fresh);
    }
    this.expandAvailabilityGroup();
  }

  expandAvailabilityGroup() {
    const trigger = this.panel.querySelector('[data-controls="CollectionSidebar-1"]');
    if (!trigger) {
      return;
    }
    // Wait until theme.collapsibles has initialized the trigger (sets
    // aria-expanded); if it is already expanded the poll times out harmlessly.
    this.waitFor(() => trigger.getAttribute('aria-expanded') === 'false').then((collapsed) => {
      if (!collapsed || !trigger.isConnected) {
        return;
      }
      trigger.click();
      const group = trigger.closest('.collection-sidebar__group');
      const content = group && group.querySelector('[data-id="CollectionSidebar-1"]');
      if (content) {
        content.style.height = 'auto';
      }
    });
  }

  placeChips() {
    if (this.disabled) {
      return;
    }
    if (this.desktopMq.matches) {
      const content = this.ajaxContent.querySelector('.grid__item--content');
      if (content && this.chips.parentElement !== content) {
        content.insertAdjacentElement('afterbegin', this.chips);
      }
    } else if (this.chipsHome && this.chips.parentElement !== this.chipsHome) {
      this.chipsHome.appendChild(this.chips);
    }
  }

  /* ── Events ────────────────────────────────────────────────────────────── */

  bindEvents() {
    if (this.filterBtn) {
      this.filterBtn.addEventListener('click', () => this.toggleDrawer());
    }

    // Chips proxy the matching panel option, so chip and panel state never diverge
    this.chips.addEventListener('click', (evt) => {
      const chip = evt.target.closest('[data-ds04-chip]');
      if (!chip) {
        return;
      }
      const option = this.findOption(chip.dataset.key, chip.dataset.value);
      if (option) {
        this.onOptionToggle(option);
      }
    });

    // One delegated listener on the held panel node — survives re-adoption.
    // The relocated native filter form's own inputs don't match these selectors,
    // so theme.js keeps handling them untouched.
    this.panel.addEventListener('click', (evt) => {
      const option = evt.target.closest('[data-ds04-option]');
      const header = evt.target.closest('[data-ds04-group-toggle]');
      const close = evt.target.closest('[data-ds04-close]');
      const apply = evt.target.closest('[data-ds04-apply]');
      const clear = evt.target.closest('[data-ds04-clear]');
      if (option) {
        this.onOptionToggle(option);
      } else if (header) {
        this.toggleGroup(header);
      } else if (close || apply) {
        this.closeDrawer();
      } else if (clear) {
        this.clearAll();
      }
    });

    // Re-home the chips when the viewport crosses the breakpoint. The resize
    // fallback covers environments where MediaQueryList change events are
    // unreliable; placeChips() is a no-op when the chips are already home.
    this.desktopMq.addEventListener('change', () => this.placeChips());
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.placeChips(), 200);
    });
  }

  onOptionToggle(option) {
    const { key, value } = option.dataset;
    if (key === 'price') {
      this.onPriceToggle(option, value);
      return;
    }
    if (!Array.isArray(this.state[key])) {
      return;
    }
    const isActive = option.classList.toggle(OPTION_ACTIVE_CLASS);
    option.setAttribute('aria-pressed', String(isActive));
    if (isActive && !this.state[key].includes(value)) {
      this.state[key].push(value);
    } else if (!isActive) {
      this.state[key] = this.state[key].filter((v) => v !== value);
    }
    this.applyFilters(false);
    this.syncChips();
  }

  onPriceToggle(option, value) {
    // Single-select: activating one price range deactivates the previous; re-tapping
    // the active one clears it. Price filters client-side on the card's displayed price
    // (data-filter-price) — instant, no server round-trip.
    const group = option.closest('[data-ds04-group]');
    const previous = group.querySelector(`.${OPTION_ACTIVE_CLASS}`);
    const wasActive = option.classList.contains(OPTION_ACTIVE_CLASS);
    if (previous && previous !== option) {
      previous.classList.remove(OPTION_ACTIVE_CLASS);
      previous.setAttribute('aria-pressed', 'false');
    }
    option.classList.toggle(OPTION_ACTIVE_CLASS, !wasActive);
    option.setAttribute('aria-pressed', String(!wasActive));
    this.state.price = wasActive ? '' : value;
    this.applyFilters(false);
    this.syncChips();
  }

  toggleGroup(header) {
    const collapsed = header.classList.toggle('ds04__group-header--collapsed');
    header.setAttribute('aria-expanded', String(!collapsed));
  }

  /* ── Client-side filtering ─────────────────────────────────────────────── */

  applyFilters(skipFade) {
    const { type, concern, position, badge, price } = this.state;
    this.ajaxContent.querySelectorAll('.product-card-v4').forEach((card) => {
      const tax = this.cardTaxonomy(card);
      const untagged =
        !tax.type.length && !tax.concern.length && !tax.position.length && !tax.badge.length;
      // AND across groups: a card must satisfy EVERY active group. Within a group it is
      // still OR (any selected value matches). Inactive groups don't constrain, so with
      // nothing selected every card matches. Price is a further AND on top. Untagged /
      // "Other" cards stay visible regardless (production's "Other always shows").
      const matched =
        this.groupMatches(tax.type, type) &&
        this.groupMatches(tax.concern, concern) &&
        this.groupMatches(tax.position, position) &&
        this.groupMatches(tax.badge, badge);
      const taxonomyOk = untagged || matched;
      card.classList.toggle(CARD_HIDDEN_CLASS, !(taxonomyOk && this.priceInRange(card, price)));
    });
    this.updateCount();
    if (!skipFade) {
      this.fadeGrid();
    }
  }

  groupMatches(cardValues, selected) {
    // An inactive group (nothing selected) never constrains; otherwise the card must
    // share at least one value with the selection (within-group OR).
    return selected.length === 0 || this.intersects(cardValues, selected);
  }

  priceInRange(card, price) {
    if (!price) {
      return true;
    }
    const cents = parseInt(card.dataset.filterPrice, 10);
    if (Number.isNaN(cents)) {
      return true; // no price data → don't hide (fail open, like an untagged card)
    }
    const [lo, hi] = price.split('-').map(Number);
    const dollars = cents / 100;
    return dollars >= lo && dollars <= hi;
  }

  buildVocab() {
    const vocab = { type: new Set(), concern: new Set(), position: new Set(), badge: new Set() };
    if (!this.panel) {
      return vocab;
    }
    this.panel.querySelectorAll('[data-ds04-option]').forEach((option) => {
      const set = vocab[option.dataset.key];
      if (set) {
        // 'price' options have no Set here, so they're skipped (price isn't a card tag).
        set.add(option.dataset.value);
      }
    });
    return vocab;
  }

  cardTaxonomy(card) {
    // Tags win. A product with no ds-* tags (data-ds-untagged) falls back to the
    // hardcoded production mapping by handle, so filters work before the catalog is
    // tagged. The fallback is used whole — including type — so a product whose
    // product_type doesn't match its curated type (e.g. a wedge pillow with
    // product_type "Pillows") still filters the way it does in production.
    if (card.dataset.dsUntagged === 'true') {
      const fallback = FALLBACK_TAXONOMY[card.dataset.productHandle];
      if (fallback) {
        return fallback;
      }
    }
    return {
      type: this.readList(card, 'filterType', 'type'),
      concern: this.readList(card, 'filterConcerns', 'concern'),
      position: this.readList(card, 'filterPositions', 'position'),
      badge: this.readList(card, 'filterBadge', 'badge'),
    };
  }

  readList(card, datasetProp, dimension) {
    const raw = (card.dataset[datasetProp] || '').split(/\s+/).filter(Boolean);
    const allowed = this.vocab[dimension];
    const valid = [];
    const unknown = [];
    raw.forEach((value) => {
      (allowed.has(value) ? valid : unknown).push(value);
    });
    // Drop unrecognized tag values so a mistagged card fails OPEN (an unknown tag
    // never counts as "tagged", so the card stays visible) instead of silently
    // disappearing under a filter. Warn once per card+dimension so the bad tag is
    // debuggable without spamming on every re-render.
    if (unknown.length) {
      this.warnUnknownTags(card, dimension, unknown);
    }
    return valid;
  }

  warnUnknownTags(card, dimension, unknown) {
    const key = `${card.dataset.productHandle}:${dimension}`;
    if (this.warnedTags.has(key)) {
      return;
    }
    this.warnedTags.add(key);
    console.warn(
      `DS_04: product "${card.dataset.productHandle}" has unrecognized ${dimension} ` +
        `value(s): ${unknown.join(', ')} — ignored, product stays visible. ` +
        `Fix the ds-${dimension}:* tag to a canonical slug.`
    );
  }

  intersects(cardValues, selectedValues) {
    return selectedValues.some((value) => cardValues.includes(value));
  }

  updateCount() {
    const countEl = document.querySelector('[data-collection-count]');
    if (!countEl) {
      return;
    }
    const visible = this.ajaxContent.querySelectorAll(
      `.product-card-v4:not(.${CARD_HIDDEN_CLASS})`
    ).length;
    countEl.textContent = `${visible} product${visible === 1 ? '' : 's'}`;
  }

  fadeGrid() {
    const grid = this.ajaxContent.querySelector('.product-grid-v4');
    if (!grid) {
      return;
    }
    grid.classList.add(GRID_FADED_CLASS);
    setTimeout(() => grid.classList.remove(GRID_FADED_CLASS), FADE_MS);
  }

  syncChips() {
    this.chips.querySelectorAll('[data-ds04-chip]').forEach((chip) => {
      const list = this.state[chip.dataset.key];
      const active = Array.isArray(list) && list.includes(chip.dataset.value);
      chip.classList.toggle(CHIP_ACTIVE_CLASS, active);
      chip.setAttribute('aria-pressed', String(active));
    });
  }

  findOption(key, value) {
    return this.panel.querySelector(`[data-ds04-option][data-key="${key}"][data-value="${value}"]`);
  }

  /* ── Drawer ────────────────────────────────────────────────────────────── */

  toggleDrawer() {
    const open = this.sectionRoot.classList.toggle(DRAWER_OPEN_CLASS);
    this.filterBtn.setAttribute('aria-expanded', String(open));
  }

  closeDrawer() {
    this.sectionRoot.classList.remove(DRAWER_OPEN_CLASS);
    if (this.filterBtn) {
      this.filterBtn.setAttribute('aria-expanded', 'false');
    }
  }

  /* ── Clear All (won path) ──────────────────────────────────────────────── */

  clearAll() {
    this.panel.querySelectorAll(`.${OPTION_ACTIVE_CLASS}`).forEach((el) => {
      el.classList.remove(OPTION_ACTIVE_CLASS);
      el.setAttribute('aria-pressed', 'false');
    });
    this.state = { type: [], concern: [], position: [], badge: [], price: '' };
    this.syncChips();

    // If the native Availability filter (relocated into our panel) is active, clear it
    // via theme.js's own active-tags clear link; the observer re-applies our emptied
    // state on the fresh grid. Price/taxonomy are client-side, so this only fires when a
    // native filter is genuinely active.
    const activeTagLink = document.querySelector('.tag-list--active-tags a.btn');
    if (activeTagLink) {
      activeTagLink.href = '/collections/all';
      activeTagLink.click();
      return;
    }

    this.applyFilters(false);
  }

  /* ── Re-render survival + pagination guard ─────────────────────────────── */

  onGridReplaced() {
    if (this.disabled || this.checkPaginationBail()) {
      return;
    }
    if (this.intelligemsTestActive()) {
      this.disable('the Intelligems DS_04 experiment re-injected after an AJAX render');
      return;
    }
    this.mount();
    // theme.js onReplaceAjaxContent just rewrote [data-collection-count] with a
    // bare number; applyFilters re-hides cards on the fresh grid and re-fixes it.
    this.applyFilters(true);
  }

  checkPaginationBail() {
    if (!this.ajaxContent.querySelector('.pagination')) {
      return false;
    }
    this.disable(
      'pagination detected — client-side filtering cannot see other pages. Raise paginate_by in main-collection-v4.liquid or trim the collection.'
    );
    return true;
  }

  disable(reason) {
    if (this.disabled) {
      return;
    }
    this.disabled = true;
    this.closeDrawer();
    // Put the native filter form back where the theme expects it
    const wrapper = this.panel.querySelector('.filter-wrapper');
    const sidebarWrap = document.getElementById('CollectionSidebarFilterWrap');
    if (wrapper && sidebarWrap) {
      sidebarWrap.appendChild(wrapper);
    }
    this.panel.remove();
    this.chips.remove();
    this.root.hidden = true;
    this.sectionRoot.classList.remove('ds04-collection');
    this.ajaxContent.querySelectorAll(`.${CARD_HIDDEN_CLASS}`).forEach((card) => {
      card.classList.remove(CARD_HIDDEN_CLASS);
    });
    if (this.observer) {
      this.observer.disconnect();
    }
    console.warn(`DS_04: filter UI disabled — ${reason}`);
  }

  /* ── Utils ─────────────────────────────────────────────────────────────── */

  waitFor(condition) {
    return new Promise((resolve) => {
      const first = condition();
      if (first) {
        resolve(first);
        return;
      }
      let elapsed = 0;
      const timer = setInterval(() => {
        elapsed += POLL_INTERVAL;
        const value = condition();
        if (value) {
          clearInterval(timer);
          resolve(value);
        } else if (elapsed >= POLL_TIMEOUT) {
          clearInterval(timer);
          resolve(null);
        }
      }, POLL_INTERVAL);
    });
  }
}

// readyState guard: with `defer` this normally runs before DOMContentLoaded, but
// the script can also be (re)executed after page load (e.g. theme-dev hot reload),
// where a bare DOMContentLoaded listener would never fire. The instance handle on
// the root node doubles as an idempotence guard and a QA hook.
function ds04Boot() {
  const root = document.querySelector('[data-ds04-root]');
  if (root && !root.ds04Instance) {
    root.ds04Instance = new Ds04CollectionFilters(root);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', ds04Boot);
} else {
  ds04Boot();
}
